import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";
import { ApiServices } from "../../utils/ApiService";

export default function FileFormatComponent() {
  const { enqueueSnackbar } = useSnackbar();

  const [allFormats, setAllFormats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    docCategory: "",
    docType: "",
    possession: "",
    fromOrThereafter: "",
  });

  const [formatContent, setFormatContent] = useState("");

  // ----------------------------------
  // Load all formats once
  // ----------------------------------
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await ApiServices.get("file-format/file-format-list");
        setAllFormats(res?.data?.fileFormats || []);
      } catch (err) {
        enqueueSnackbar("Error loading formats", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [enqueueSnackbar]);

  // ----------------------------------
  // Helpers
  // ----------------------------------
  const getUnique = (arr, key) => {
    return [...new Map(arr.map(item => [item[key] ?? "null", item])).values()];
  };

  // ----------------------------------
  // Dropdown Options
  // ----------------------------------
  const categoryOptions = useMemo(() => {
    return getUnique(allFormats, "doc_category_id");
  }, [allFormats]);

  const typeOptions = useMemo(() => {
    return getUnique(
      allFormats.filter(f =>
        !filters.docCategory || f.doc_category_id === filters.docCategory
      ),
      "doc_type_id"
    );
  }, [allFormats, filters.docCategory]);

  const possessionOptions = useMemo(() => {
    return getUnique(
      allFormats.filter(f =>
        (!filters.docCategory || f.doc_category_id === filters.docCategory) &&
        (!filters.docType || f.doc_type_id === filters.docType)
      ),
      "doc_possession_id"
    );
  }, [allFormats, filters.docCategory, filters.docType]);

  const fromOptions = useMemo(() => {
    return getUnique(
      allFormats.filter(f =>
        (!filters.docCategory || f.doc_category_id === filters.docCategory) &&
        (!filters.docType || f.doc_type_id === filters.docType) &&
        ((f.doc_possession_id ?? null) === (filters.possession || null))
      ),
      "from_or_thereafter"
    );
  }, [allFormats, filters]);

  // ----------------------------------
  // Handle Filter Change
  // ----------------------------------
  const handleFilterChange = (key, value) => {
    const parsedValue =
      key === "fromOrThereafter"
        ? value
        : value === ""
        ? ""
        : Number(value);

    setFilters(prev => {
      const updated = { ...prev, [key]: parsedValue };

      if (key === "docCategory") {
        updated.docType = "";
        updated.possession = "";
        updated.fromOrThereafter = "";
      }

      if (key === "docType") {
        updated.possession = "";
        updated.fromOrThereafter = "";
      }

      if (key === "possession") {
        updated.fromOrThereafter = "";
      }

      return updated;
    });
  };

  // ----------------------------------
  // Get Format (NO API CALL)
  // ----------------------------------
  useEffect(() => {
    const result = allFormats.find(f =>
      f.doc_category_id === filters.docCategory &&
      f.doc_type_id === filters.docType &&
      (f.doc_possession_id ?? null) === (filters.possession || null) &&
      f.from_or_thereafter === filters.fromOrThereafter
    );

    setFormatContent(result?.format || "");
  }, [filters, allFormats]);

  // ----------------------------------
  // Copy
  // ----------------------------------
  const handleCopy = () => {
    if (!formatContent) return;
    navigator.clipboard.writeText(formatContent);
    enqueueSnackbar("Copied to clipboard", { variant: "success" });
  };

  // ----------------------------------
  // Reset
  // ----------------------------------
  const handleReset = () => {
    setFilters({
      docCategory: "",
      docType: "",
      possession: "",
      fromOrThereafter: "",
    });
    setFormatContent("");
  };

  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        {/* Category */}
        <FormControl sx={{ minWidth: "25%" }} size="small">
          <InputLabel>Doc Category</InputLabel>
          <Select
            value={filters.docCategory}
            label="Doc Category"
            onChange={(e) => handleFilterChange("docCategory", e.target.value)}
          >
            {categoryOptions.map(item => (
              <MenuItem key={item.doc_category_id} value={item.doc_category_id}>
                {item.doc_category_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Type */}
        <FormControl sx={{ minWidth: "25%" }} size="small">
          <InputLabel>Doc Type</InputLabel>
          <Select
            value={filters.docType}
            label="Doc Type"
            disabled={!filters.docCategory}
            onChange={(e) => handleFilterChange("docType", e.target.value)}
          >
            {typeOptions.map(item => (
              <MenuItem key={item.doc_type_id} value={item.doc_type_id}>
                {item.doc_type_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Possession */}
        <FormControl sx={{ minWidth: "20%" }} size="small">
          <InputLabel>Possession</InputLabel>
          <Select
            value={filters.possession}
            label="Possession"
            disabled={!filters.docType}
            onChange={(e) => handleFilterChange("possession", e.target.value)}
          >
            {possessionOptions.map(item => (
              <MenuItem
                key={item.doc_possession_id ?? "none"}
                value={item.doc_possession_id ?? ""}
              >
                {item.doc_possession_name || "None"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* From */}
        <FormControl sx={{ minWidth: "25%" }} size="small">
          <InputLabel>From / Thereafter</InputLabel>
          <Select
            value={filters.fromOrThereafter}
            label="From / Thereafter"
            disabled={!filters.possession}
            onChange={(e) =>
              handleFilterChange("fromOrThereafter", e.target.value)
            }
          >
            {fromOptions.map(item => (
              <MenuItem
                key={item.from_or_thereafter}
                value={item.from_or_thereafter}
              >
                {item.from_or_thereafter}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Buttons */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          disabled={!formatContent}
          onClick={handleCopy}
        >
          Copy
        </Button>

        <Button variant="contained" onClick={handleReset}>
          Reset
        </Button>
      </Box>

      {/* Output */}
      <TextField
        label="Format"
        multiline
        rows={10}
        value={formatContent}
        fullWidth
        InputProps={{ 
          readOnly: true,
          sx: {
            backgroundColor: "#fff"
          }
         }}
      />
    </Box>
  );
}